import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock, 
  CalendarIcon, 
  History,
  FileEdit,
  Coffee,
  UtensilsCrossed,
  ArrowLeft,
  Bell,
  LogOut,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Timer,
  Plus,
  Trash2
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';
import NotificationPanel from '@/components/NotificationPanel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeHistory = () => {
  const { user, logout } = useAuth();
  const [history, setHistory] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({
    date: '',
    requested_clock_in: '',
    requested_clock_out: '',
    requested_breaks: [], // Dynamic breaks array: [{break_type, start_time, end_time}]
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchHistory = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) return;
    
    setLoading(true);
    try {
      const [historyRes, correctionsRes, notifRes] = await Promise.all([
        axios.post(`${API}/history`, {
          start_date: dateRange.from.toISOString(),
          end_date: dateRange.to.toISOString()
        }),
        axios.get(`${API}/shift-corrections`),
        axios.get(`${API}/notifications/unread-count`)
      ]);
      setHistory(historyRes.data.history || []);
      setCorrections(correctionsRes.data || []);
      setUnreadCount(notifRes.data.count);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const openCorrectionModal = (entry = null) => {
    if (entry) {
      // Use local time strings for the form inputs
      const clockInTime = entry.clock_in ? new Date(entry.clock_in).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
      const clockOutTime = entry.clock_out ? new Date(entry.clock_out).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
      
      // Convert all existing breaks to form format
      const formattedBreaks = (entry.breaks || []).map(b => ({
        break_type: b.type || 'break',
        start_time: b.start_time ? new Date(b.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
        end_time: b.end_time ? new Date(b.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''
      }));
      
      // Get local date from clock_in timestamp
      const localDate = entry.clock_in ? format(new Date(entry.clock_in), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
      
      setCorrectionForm({
        date: localDate,
        requested_clock_in: clockInTime,
        requested_clock_out: clockOutTime,
        requested_breaks: formattedBreaks,
        reason: ''
      });
      setSelectedEntry(entry);
    } else {
      setCorrectionForm({
        date: format(new Date(), 'yyyy-MM-dd'),
        requested_clock_in: '',
        requested_clock_out: '',
        requested_breaks: [],
        reason: ''
      });
      setSelectedEntry(null);
    }
    setShowCorrectionModal(true);
  };

  const handleSubmitCorrection = async () => {
    if (!correctionForm.date || !correctionForm.reason) {
      toast.error('Please provide a date and reason');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/shift-corrections`, correctionForm);
      toast.success('Correction request submitted!');
      setShowCorrectionModal(false);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit correction');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-[hsl(var(--success))] text-white">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Format date in user's local timezone
  const formatDateLocal = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return format(date, 'dd/MM/yyyy');
  };

  // Get the local date from a UTC timestamp (for grouping/display)
  const getLocalDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    // Return as local date string YYYY-MM-DD
    return format(date, 'yyyy-MM-dd');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.history.back()}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-sm text-slate-500">Time History</p>
              <p className="font-semibold font-['Manrope']">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setShowNotifications(true)}
              data-testid="notification-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Company Badge */}
        {user?.company_name && (
          <div className="flex justify-center">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border text-sm text-slate-600">
              <Building2 className="w-4 h-4" />
              {user.company_name}
            </div>
          </div>
        )}

        {/* Date Range Picker */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <History className="w-5 h-5" />
              Shift History
            </CardTitle>
            <CardDescription>View your past time entries and request corrections</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !dateRange.from && "text-muted-foreground"
                      )}
                      data-testid="history-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.from ? format(dateRange.from, "dd/MM/yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.from}
                      onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !dateRange.to && "text-muted-foreground"
                      )}
                      data-testid="history-end-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.to ? format(dateRange.to, "dd/MM/yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.to}
                      onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex items-end">
                <Button 
                  onClick={() => openCorrectionModal()} 
                  className="w-full gap-2 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                  data-testid="new-correction-btn"
                >
                  <FileEdit className="w-4 h-4" />
                  Request Correction
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* History List */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b">
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Time Entries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : history.length === 0 ? (
              <div className="empty-state py-12">
                <Clock className="empty-state-icon" />
                <p className="text-slate-500">No time entries found</p>
                <p className="text-sm text-slate-400">Select a different date range</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="divide-y">
                  {history.map((entry) => (
                    <div 
                      key={entry.id} 
                      className="p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`history-entry-${entry.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{formatDateLocal(entry.clock_in)}</p>
                            {entry.office_name && (
                              <Badge variant="outline" className="text-xs">{entry.office_name}</Badge>
                            )}
                            {entry.is_offline && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Offline</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500">Clock In</p>
                              <p className="font-mono">{formatTime(entry.clock_in)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Clock Out</p>
                              <p className="font-mono">{entry.clock_out ? formatTime(entry.clock_out) : '-'}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Gross Hours</p>
                              <p className="font-mono">{entry.gross_hours?.toFixed(2) || '-'}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Net Hours</p>
                              <p className="font-mono text-[hsl(var(--success))]">{entry.net_hours?.toFixed(2) || '-'}</p>
                            </div>
                          </div>
                          {entry.breaks && entry.breaks.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {entry.breaks.map((brk, idx) => (
                                <div key={idx} className="flex items-center gap-1 text-xs bg-slate-100 px-2 py-1 rounded">
                                  {brk.type === 'breakfast' ? (
                                    <Coffee className="w-3 h-3" />
                                  ) : brk.type === 'lunch' ? (
                                    <UtensilsCrossed className="w-3 h-3" />
                                  ) : (
                                    <Timer className="w-3 h-3" />
                                  )}
                                  <span className="capitalize">{brk.type}:</span>
                                  <span className="font-mono">{formatTime(brk.start_time)} - {brk.end_time ? formatTime(brk.end_time) : 'ongoing'}</span>
                                  {brk.duration_minutes && (
                                    <span className="text-slate-500">({Math.round(brk.duration_minutes)}m)</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCorrectionModal(entry)}
                          data-testid={`request-correction-${entry.id}`}
                        >
                          <FileEdit className="w-4 h-4 mr-1" />
                          Correct
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Correction Requests */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b">
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <FileEdit className="w-5 h-5" />
              My Correction Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {corrections.length === 0 ? (
              <div className="empty-state py-8">
                <FileEdit className="empty-state-icon" />
                <p className="text-slate-500">No correction requests</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[300px]">
                <div className="divide-y">
                  {corrections.map((correction) => (
                    <div 
                      key={correction.id} 
                      className="p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`correction-${correction.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">Correction for {correction.date}</p>
                            {getStatusBadge(correction.status)}
                          </div>
                          <p className="text-sm text-slate-500">{correction.reason}</p>
                          {correction.admin_notes && (
                            <p className="text-sm text-slate-600 italic">Admin: {correction.admin_notes}</p>
                          )}
                          <p className="text-xs text-slate-400">
                            Submitted: {format(new Date(correction.created_at), 'dd/MM/yyyy HH:mm')}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          {correction.status === 'pending' && <AlertCircle className="w-5 h-5 text-yellow-500" />}
                          {correction.status === 'approved' && <CheckCircle className="w-5 h-5 text-green-500" />}
                          {correction.status === 'rejected' && <XCircle className="w-5 h-5 text-red-500" />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Correction Modal */}
      <Dialog open={showCorrectionModal} onOpenChange={setShowCorrectionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Request Shift Correction</DialogTitle>
            <DialogDescription>
              {selectedEntry 
                ? `Edit times for ${formatDateLocal(selectedEntry.clock_in)}`
                : 'Request a correction for a missing or incorrect time entry'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={correctionForm.date}
                onChange={(e) => setCorrectionForm(prev => ({ ...prev, date: e.target.value }))}
                disabled={!!selectedEntry}
                data-testid="correction-date"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clock In Time</Label>
                <Input
                  type="time"
                  value={correctionForm.requested_clock_in}
                  onChange={(e) => setCorrectionForm(prev => ({ ...prev, requested_clock_in: e.target.value }))}
                  data-testid="correction-clock-in"
                />
              </div>
              <div className="space-y-2">
                <Label>Clock Out Time</Label>
                <Input
                  type="time"
                  value={correctionForm.requested_clock_out}
                  onChange={(e) => setCorrectionForm(prev => ({ ...prev, requested_clock_out: e.target.value }))}
                  data-testid="correction-clock-out"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Breaks
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCorrectionForm(prev => ({
                    ...prev,
                    requested_breaks: [...prev.requested_breaks, { break_type: 'break', start_time: '', end_time: '' }]
                  }))}
                  data-testid="add-break-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Break
                </Button>
              </div>
              
              {correctionForm.requested_breaks.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-2">No breaks recorded. Click "Add Break" to add one.</p>
              ) : (
                <div className="space-y-2">
                  {correctionForm.requested_breaks.map((brk, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <Select
                        value={brk.break_type}
                        onValueChange={(value) => {
                          const newBreaks = [...correctionForm.requested_breaks];
                          newBreaks[idx] = { ...newBreaks[idx], break_type: value };
                          setCorrectionForm(prev => ({ ...prev, requested_breaks: newBreaks }));
                        }}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="breakfast">
                            <span className="flex items-center gap-1"><Coffee className="w-3 h-3" /> Breakfast</span>
                          </SelectItem>
                          <SelectItem value="lunch">
                            <span className="flex items-center gap-1"><UtensilsCrossed className="w-3 h-3" /> Lunch</span>
                          </SelectItem>
                          <SelectItem value="break">
                            <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> Break</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="time"
                        placeholder="Start"
                        value={brk.start_time}
                        onChange={(e) => {
                          const newBreaks = [...correctionForm.requested_breaks];
                          newBreaks[idx] = { ...newBreaks[idx], start_time: e.target.value };
                          setCorrectionForm(prev => ({ ...prev, requested_breaks: newBreaks }));
                        }}
                        className="w-24"
                        data-testid={`correction-break-${idx}-start`}
                      />
                      <span className="text-slate-400">-</span>
                      <Input
                        type="time"
                        placeholder="End"
                        value={brk.end_time}
                        onChange={(e) => {
                          const newBreaks = [...correctionForm.requested_breaks];
                          newBreaks[idx] = { ...newBreaks[idx], end_time: e.target.value };
                          setCorrectionForm(prev => ({ ...prev, requested_breaks: newBreaks }));
                        }}
                        className="w-24"
                        data-testid={`correction-break-${idx}-end`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 px-2"
                        onClick={() => {
                          const newBreaks = correctionForm.requested_breaks.filter((_, i) => i !== idx);
                          setCorrectionForm(prev => ({ ...prev, requested_breaks: newBreaks }));
                        }}
                        data-testid={`remove-break-${idx}-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Reason for Correction</Label>
              <Textarea
                placeholder="Explain why this correction is needed..."
                value={correctionForm.reason}
                onChange={(e) => setCorrectionForm(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="correction-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitCorrection}
              disabled={submitting || !correctionForm.reason}
              className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
              data-testid="submit-correction-btn"
            >
              {submitting ? <div className="spinner" /> : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Panel */}
      <NotificationPanel 
        open={showNotifications} 
        onClose={() => setShowNotifications(false)}
        onRead={() => setUnreadCount(prev => Math.max(0, prev - 1))}
      />
    </div>
  );
};

export default EmployeeHistory;
